import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Film, ArrowRight, Search } from "lucide-react";
import VideoCard from "../../components/public/VideoCard";
import { VIDEO_LIBRARY, VIDEO_CATEGORIES } from "../../data/videoLibrary";
import { useDocMeta } from "../../lib/useDocMeta";
import { track, EVENT } from "../../lib/publicAnalytics";

export default function DemoLibrary() {
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("All");

  useDocMeta({
    title: "Demo Library · GovBidIQ",
    description: "Short videos covering every part of GovBidIQ — prospect intelligence, opportunity scoring, set-aside strategy, capture, and proposal use cases.",
    path: "/demo-library",
  });

  useEffect(() => { track(EVENT.DEMO_LIBRARY_VIEWED, {}); }, []);

  const filtered = useMemo(() => {
    const ql = q.toLowerCase();
    return VIDEO_LIBRARY.filter((v) =>
      (cat === "All" || v.category === cat) &&
      (!q || (v.title + " " + v.description + " " + v.category).toLowerCase().includes(ql))
    );
  }, [q, cat]);

  return (
    <div className="max-w-[1300px] mx-auto px-6 py-14">
      <div className="font-mono text-[10px] tracking-widest uppercase text-sky-400 mb-3 inline-flex items-center gap-1.5"><Film className="w-3 h-3" /> / Demo Library</div>
      <h1 className="font-display font-black text-4xl sm:text-5xl text-slate-100 leading-tight mb-3">Twelve short demos.</h1>
      <p className="text-slate-400 text-lg max-w-2xl leading-relaxed mb-8">
        Watch the platform work. Each clip is 1–5 minutes. Placeholder thumbnails for now — final cuts ship as we record them.
      </p>

      <div className="flex flex-col md:flex-row gap-3 mb-6">
        <div className="relative flex-1">
          <Search className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search videos…" data-testid="demo-search" aria-label="Search videos" className="w-full bg-[var(--bg-secondary)] border border-slate-700 focus:border-sky-500 outline-none text-slate-100 text-sm pl-10 pr-3 py-2.5 font-mono" />
        </div>
        <select value={cat} onChange={(e) => setCat(e.target.value)} data-testid="demo-category" aria-label="Filter by category" className="bg-[var(--bg-secondary)] border border-slate-700 focus:border-sky-500 outline-none text-slate-100 text-sm px-3 py-2.5 font-mono">
          <option value="All">All Categories</option>
          {VIDEO_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
      </div>

      {filtered.length === 0 && (
        <div className="text-center py-12 text-slate-500 font-mono text-sm">No videos match your search.</div>
      )}

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-[var(--border-soft)] mb-10">
        {filtered.map((v) => (
          <div key={v.slug} className="flex flex-col">
            <VideoCard slug={v.slug} title={v.title} description={v.description} provider={v.provider} src={v.src} />
            <div className="bg-[var(--bg-secondary)] border-t-0 border border-[var(--border-soft)] px-4 pb-3 flex items-center justify-between text-[10px] font-mono">
              <span className="text-slate-500 tracking-widest uppercase">{v.category} · {v.watch_minutes} min</span>
              {v.related_to && (
                <Link to={v.related_to} className="text-sky-400 hover:text-sky-300 tracking-widest uppercase inline-flex items-center gap-1" data-testid={`demo-related-${v.slug}`}>
                  Related <ArrowRight className="w-3 h-3" />
                </Link>
              )}
            </div>
          </div>
        ))}
      </div>

      <div className="border border-sky-500/30 bg-sky-500/5 p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <div className="font-display font-bold text-slate-100 text-lg mb-0.5">Not sure where to start?</div>
          <div className="text-sm text-slate-400">Take AI Discovery first — we'll point you at the right video.</div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Link to="/discovery" data-testid="demo-cta-discovery" className="inline-flex items-center gap-2 bg-sky-500 hover:bg-sky-400 text-slate-950 font-mono text-[11px] tracking-[0.2em] uppercase font-bold px-4 py-2.5">
            Start AI Discovery <ArrowRight className="w-3.5 h-3.5" />
          </Link>
          <Link to="/strategy-call" data-testid="demo-cta-strategy" className="inline-flex items-center gap-2 border border-sky-500/60 hover:bg-sky-500/10 text-sky-300 font-mono text-[11px] tracking-[0.2em] uppercase font-bold px-4 py-2.5">
            Strategy Call
          </Link>
        </div>
      </div>
    </div>
  );
}
